import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { AuthMe } from "@icraftx/shared/v1";
import { Button } from "@/components/ui/button";
import { fetchMe } from "@/shared/api/auth";
import { filterDocIds } from "@/shared/api/tags";
import { EmptyState } from "../EmptyState";
import { PageFrame } from "../PageFrame";
import { PageHeader } from "../PageHeader";
import {
  canSeeCompanyNav,
  companyNavForPermissions,
  shopDayHint,
} from "../nav-math";
import { hasActionPermission } from "../permission-client";
import { loadRecentDocs, recentDocHint } from "../recent-docs";
import { useShellBadges } from "../useShellBadges";

export function CompanyHomePage() {
  const { companySlug } = useParams();
  const navigate = useNavigate();
  const [me, setMe] = useState<AuthMe | null>(null);
  const [assignedSo, setAssignedSo] = useState(0);
  const [assignedPo, setAssignedPo] = useState(0);
  const [recentTick, setRecentTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetchMe()
      .then((data) => {
        if (cancelled) return;
        const allowed =
          data.user.isSuperadmin ||
          data.companies.some((c) => c.slug === companySlug);
        if (!allowed) {
          navigate("/select-company", { replace: true });
          return;
        }
        setMe(data);
      })
      .catch(() => {
        if (!cancelled) navigate("/login", { replace: true });
      });
    return () => {
      cancelled = true;
    };
  }, [companySlug, navigate]);

  useEffect(() => {
    setRecentTick((n) => n + 1);
  }, [companySlug]);

  const company = me?.companies.find((c) => c.slug === companySlug);
  const held =
    me?.user.isSuperadmin && !company?.permissions?.length
      ? (["*"] as string[])
      : (company?.permissions ?? []);
  const effectiveHeld = held.length
    ? held
    : me?.user.isSuperadmin
      ? (["*"] as string[])
      : [];
  const hint = shopDayHint(effectiveHeld);
  const { primary, more } = companyNavForPermissions(effectiveHeld);
  const { itemLabel, inboxUnread, approvalsPending } = useShellBadges({
    companySlug,
    ready: Boolean(me),
    held: effectiveHeld,
    poll: true,
  });

  const heldKey = effectiveHeld.slice().sort().join(",");

  useEffect(() => {
    if (!companySlug || !me) return;
    let cancelled = false;
    const heldList = heldKey ? heldKey.split(",") : [];
    const canSales = canSeeCompanyNav(heldList, "sales");
    const canPurchasing = canSeeCompanyNav(heldList, "purchasing");
    void (async () => {
      try {
        const [so, po] = await Promise.all([
          canSales
            ? filterDocIds(companySlug, {
                docType: "sales_order",
                assignedToMe: true,
              })
            : Promise.resolve({ docIds: [] as string[] }),
          canPurchasing
            ? filterDocIds(companySlug, {
                docType: "purchase_order",
                assignedToMe: true,
              })
            : Promise.resolve({ docIds: [] as string[] }),
        ]);
        if (cancelled) return;
        setAssignedSo(so.docIds?.length ?? 0);
        setAssignedPo(po.docIds?.length ?? 0);
      } catch {
        if (!cancelled) {
          setAssignedSo(0);
          setAssignedPo(0);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companySlug, me, heldKey]);

  const recent = useMemo(() => {
    void recentTick;
    if (!companySlug) return [];
    return loadRecentDocs(companySlug);
  }, [companySlug, recentTick]);

  const canApprove = hasActionPermission(effectiveHeld, "can_approve");
  const showSalesQueues = canSeeCompanyNav(effectiveHeld, "sales");
  const showPurchasingQueues = canSeeCompanyNav(effectiveHeld, "purchasing");
  const needsMe: { to: string; label: string }[] = [];
  if (companySlug) {
    if (canSeeCompanyNav(effectiveHeld, "inbox")) {
      needsMe.push({
        to: `/c/${companySlug}/inbox`,
        label: inboxUnread > 0 ? `Inbox (${inboxUnread})` : "Inbox",
      });
    }
    if (canApprove) {
      needsMe.push({
        to: `/c/${companySlug}/approvals`,
        label:
          approvalsPending > 0
            ? `Approvals (${approvalsPending})`
            : "Approvals",
      });
    }
    if (assignedSo > 0) {
      needsMe.push({
        to: `/c/${companySlug}/sales/orders?assignee=mine`,
        label: `Assigned sales orders (${assignedSo})`,
      });
    }
    if (assignedPo > 0) {
      needsMe.push({
        to: `/c/${companySlug}/purchasing/orders?assignee=mine`,
        label: `Assigned purchase orders (${assignedPo})`,
      });
    }
    if (showSalesQueues) {
      needsMe.push({
        to: `/c/${companySlug}/sales/to-collect`,
        label: "To collect",
      });
      needsMe.push({
        to: `/c/${companySlug}/sales/to-deliver`,
        label: "To deliver",
      });
    }
    if (showPurchasingQueues) {
      needsMe.push({
        to: `/c/${companySlug}/purchasing/to-pay`,
        label: "To pay",
      });
      needsMe.push({
        to: `/c/${companySlug}/purchasing/to-receive`,
        label: "To receive",
      });
    }
  }

  if (!me) {
    return (
      <p className="m-0 text-sm text-[var(--color-muted)]">Loading company…</p>
    );
  }

  const taxLabel =
    company?.taxPriceMode === "inclusive"
      ? "Prices include tax"
      : company?.taxPriceMode === "exclusive"
        ? "Tax added at checkout"
        : null;
  const capLabel =
    company != null
      ? `Discounts over ${company.discountSoftCapPct}% need approval`
      : null;
  const meta = company
    ? [company.currencyCode, taxLabel, capLabel].filter(Boolean).join(" · ")
    : "Support view (not a member).";

  return (
    <PageFrame width="narrow">
      <PageHeader
        eyebrow="Home"
        title={company?.name ?? companySlug ?? "Company"}
        description={meta}
        actions={
          primary[0] ? (
            <Button asChild>
              <Link to={primary[0].path(companySlug!)} className="no-underline">
                {itemLabel(primary[0].id, primary[0].label)}
              </Link>
            </Button>
          ) : undefined
        }
      />

      {hint ? (
        <p className="mt-6 rounded-[var(--radius-xl)] border border-[var(--color-border)]/90 bg-[var(--color-card)]/90 px-4 py-3.5 text-sm leading-relaxed text-[var(--color-muted)] shadow-[var(--shadow-soft)]">
          {hint}
        </p>
      ) : null}

      {needsMe.length > 0 ? (
        <section className="mt-10">
          <h2 className="m-0 font-[family-name:var(--font-display)] text-base font-semibold tracking-tight text-[var(--color-foreground)]">
            Needs me
          </h2>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            Inbox, approvals, assigned work, and queues.
          </p>
          <ul className="m-0 mt-4 list-none space-y-2 p-0">
            {needsMe.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className="text-sm font-medium text-[var(--color-primary)] no-underline hover:underline"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {recent.length > 0 ? (
        <section className="mt-10">
          <h2 className="m-0 font-[family-name:var(--font-display)] text-base font-semibold tracking-tight text-[var(--color-foreground)]">
            Recent
          </h2>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            Documents you opened on this device.
          </p>
          <ul className="m-0 mt-4 list-none space-y-2 p-0">
            {recent.slice(0, 8).map((doc) => (
              <li key={`${doc.kind}-${doc.id}`}>
                <Link
                  to={doc.path}
                  className="text-sm font-medium text-[var(--color-foreground)] no-underline hover:underline"
                >
                  {doc.label}
                </Link>
                <span className="ml-2 text-xs text-[var(--color-muted)]">
                  {doc.hint ?? recentDocHint(doc.kind)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {primary.length === 0 && more.length === 0 ? (
        <div className="mt-8">
          <EmptyState
            title="No modules for this role"
            description="Ask an owner to grant POS, sales, purchasing, or inventory access."
          />
        </div>
      ) : (
        <>
          <section className="mt-10">
            <h2 className="m-0 font-[family-name:var(--font-display)] text-base font-semibold tracking-tight text-[var(--color-foreground)]">
              Today
            </h2>
            <p className="mt-1 text-sm text-[var(--color-muted)]">
              Where you usually start.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {primary.slice(primary[0] ? 1 : 0).map((item) => (
                <Button key={item.id} asChild variant="outline">
                  <Link
                    to={item.path(companySlug!)}
                    className="no-underline"
                  >
                    {itemLabel(item.id, item.label)}
                  </Link>
                </Button>
              ))}
            </div>
          </section>

          {more.length > 0 && (
            <section className="mt-10">
              <h2 className="m-0 font-[family-name:var(--font-display)] text-base font-semibold tracking-tight text-[var(--color-foreground)]">
                More
              </h2>
              <p className="mt-1 text-sm text-[var(--color-muted)]">
                Settings, books, and quieter tools.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {more.map((item) => (
                  <Button key={item.id} asChild variant="outline" size="sm">
                    <Link
                      to={item.path(companySlug!)}
                      className="no-underline"
                    >
                      {itemLabel(item.id, item.label)}
                    </Link>
                  </Button>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </PageFrame>
  );
}
